import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import type { MoverPositionStore } from './MoverPositionStore.js';

/**
 * On-demand frame invalidation for the `frameloop="demand"` canvas mode.
 *
 * When enabled, frames are only rendered in response to:
 *   - OrbitControls interaction (r3f handles this automatically)
 *   - Mover position changes (via store.onWrite → invalidate)
 *   - React re-renders that touch the scene (r3f handles this)
 *
 * Wires up the MoverPositionStore's `onWrite` callback to r3f's
 * `invalidate()` so position changes outside React trigger a frame.
 */
export function useDemandRendering(
  enabled: boolean,
  moverStore: MoverPositionStore | null,
): void {
  const invalidate = useThree((s) => s.invalidate);

  useEffect(() => {
    if (!enabled || !moverStore) return;
    moverStore.onWrite = invalidate;
    return () => {
      moverStore.onWrite = null;
    };
  }, [enabled, moverStore, invalidate]);
}
